'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import { useSiteConfig } from '@commons/SiteConfig';
import useDictionary from '@modules/Dictionary/useDictionary';
import { VTLogo } from '@icons';
import MapSidebar from './components/MapSidebar';
import './_map_app.scss';

import areas from './areas.json';

export default function MapApp() {
  const { dictionary } = useDictionary();
  const { MAPBOX_ACCESS_TOKEN } = useSiteConfig();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { products, categoriesTree } = useDesitnationCollection();
  const searchParams = useSearchParams();

  const initialCategories = useMemo(() => {
    const catParam = searchParams.get('categories');
    if (!catParam) return null;
    return catParam
      .split(',')
      .filter(Boolean)
      .map((id) => parseInt(id, 10));
  }, [searchParams]);

  const [selectedCategories, setSelectedCategories] = useState(initialCategories || []);
  const [categoriesInitialized, setCategoriesInitialized] = useState(initialCategories !== null);

  // Initialera med alla kategorier om inget sattes via URL
  useEffect(() => {
    if (!categoriesInitialized && categoriesTree?.length > 0) {
      const allCategoryIds = categoriesTree.flatMap((c) => c.children?.map((child) => child.id) || []);
      setSelectedCategories(allCategoryIds);
      setCategoriesInitialized(true);
    }
  }, [categoriesTree, categoriesInitialized]);

  const area = areas[0];
  const [lng, setLng] = useState(area.coordinates[0]);
  const [lat, setLat] = useState(area.coordinates[1]);
  const [zoom, setZoom] = useState(area.zoom);
  const [bounds, setBounds] = useState(null);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [hoveredClusterId, setHoveredClusterId] = useState(null);

  useEffect(() => {
    if (map.current || !mapContainer.current) return; // initiera kartan enbart en gång
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN || '';

    if (!mapboxgl.accessToken) {
      console.warn('Mapbox access token is missing! Lägg in MAPBOX_ACCESS_TOKEN i din .env.local');
    }
    let initialZoom = zoom;
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      initialZoom = Math.max(6, zoom - 1);
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/visittelemark/cmmamxet5006201scdjrn3iku',
      center: [lng, lat], // Center från URL-hash: 59.47/8.477 (Lat/Lng) -> [Lng, Lat] för Mapbox
      zoom: initialZoom, // Justerad för mobil/desktop
      minZoom: 6, // Begränsa utzoomning så man inte kommer för långt bort
      maxBounds: [
        [5.5, 57.5], // Sydväst [Lng, Lat]
        [11.5, 61.0] // Nordost [Lng, Lat]
      ]
    });

    // Lägg till standardkontroller för zoom (+/-) och kompass
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Lyssna på när kartan har laddat klart
    map.current.on('load', () => {
      const svgPin = `<svg width="73" height="84" viewBox="0 0 73 84" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.25" filter="url(#filter0_f_2168_4002)">
<path d="M10.5 67H66.5V73H19.5L6 78L10.5 67Z" fill="black"/>
</g>
<path d="M4.5 0H68.5V64H20.5L4.5 80V0Z" fill="#000000"/>
<defs>
<filter id="filter0_f_2168_4002" x="0" y="61" width="72.5" height="23" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
<feGaussianBlur stdDeviation="3" result="effect1_foregroundBlur_2168_4002"/>
</filter>
</defs>
</svg>`;

      const img = new Image(73, 84);
      img.onload = () => {
        if (!map.current.hasImage('custom-pin')) {
          map.current.addImage('custom-pin', img);
        }
        setMapLoaded(true);
        setBounds(map.current.getBounds());
      };
      img.onerror = () => {
        console.error('Kunde inte ladda Pin.svg för Mapbox.');
        // Starta kartan ändå så man i alla fall ser kluster
        setMapLoaded(true);
        setBounds(map.current.getBounds());
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svgPin);
    });

    // Uppdatera positions-state när rutan flyttas
    let moveTimeout;
    map.current.on('move', () => {
      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => {
        setLng(map.current.getCenter().lng.toFixed(4));
        setLat(map.current.getCenter().lat.toFixed(4));
        setZoom(map.current.getZoom().toFixed(2));
        setBounds(map.current.getBounds());
      }, 100);
    });

    return () => {
      clearTimeout(moveTimeout);
      map.current?.remove();
      map.current = null; // annars bailar init-effekten vid StrictMode-remount och kartan återskapas aldrig
      setMapLoaded(false); // nya kartans 'load' måste ge en false->true-övergång så lager-effekten kör om
    };
  }, []); // Tomt dependency array, initiera enbart en gång

  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products) || selectedCategories.length === 0) return [];
    return products.filter((p) => {
      if (!p.categories || !Array.isArray(p.categories)) return false;
      return p.categories.some((categoryId) => selectedCategories.includes(categoryId));
    });
  }, [products, selectedCategories]);

  const visibleProducts = useMemo(() => {
    if (!bounds) return filteredProducts;
    return filteredProducts.filter((p) => {
      const lat = parseFloat(p.lat);
      const lng = parseFloat(p.long);
      if (isNaN(lat) || isNaN(lng)) return false;

      return bounds.contains([lng, lat]);
    });
  }, [filteredProducts, bounds]);

  // Lägg till markers (via GeoJSON) när products uppdateras och kartan är laddad
  useEffect(() => {
    // Kontrollera att kartan är initierad, laddad och arrayen är på plats
    if (!map.current || !mapLoaded || !products || !Array.isArray(products)) return;

    const geojsonData = {
      type: 'FeatureCollection',
      features: filteredProducts
        .filter((p) => p.lat && p.long && !isNaN(parseFloat(p.lat)) && !isNaN(parseFloat(p.long)))
        .map((p) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(p.long), parseFloat(p.lat)]
          },
          properties: {
            id: p.id || '',
            title: p.title || '',
            address: p.address || '',
            image: p.image || '',
            url: p.url || ''
          }
        }))
    };

    const sourceId = 'products-source';
    const layerId = 'products-layer';
    const clusterLayerId = 'products-clusters';
    const clusterCountLayerId = 'products-cluster-count';

    // Uppdatera data om källan redan finns
    if (map.current.getSource(sourceId)) {
      map.current.getSource(sourceId).setData(geojsonData);
    } else {
      // Skapa källa
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: 14, // Maximal inzoomning för att gruppera
        clusterRadius: 50 // Radie för att gruppera
      });

      // Layer för själva kluster-cirkeln
      map.current.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#18DFBD',
          'circle-opacity': 0.5,
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            14,
            10, // Under 10 -> radie 14
            18,
            50, // Under 50 -> radie 18
            22 // Över 50 -> radie 22
          ],
          'circle-stroke-width': 0,
          'circle-stroke-color': '#fff'
        }
      });

      // Layer för antalet punkter i ett kluster
      map.current.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': 'custom-pin',
          'icon-size': 0.45,
          'icon-anchor': 'bottom-left',
          'icon-allow-overlap': true,
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 14,
          'text-offset': [1.15, -1.65], // Skjuter upp texten så den ligger i nålens kvadrat
          'text-anchor': 'center',
          'text-allow-overlap': true
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Layer för enskilda punkter (ej grupperade)
      map.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'custom-pin',
          'icon-size': 0.35, // 20-25% mindre än klustren
          'icon-anchor': 'bottom-left',
          'icon-allow-overlap': true
        }
      });

      // Hantera klick och hover på ett kluster (både cirkeln och texten inuti)
      [clusterLayerId, clusterCountLayerId].forEach((layer) => {
        map.current.on('click', layer, (e) => {
          const features = map.current.queryRenderedFeatures(e.point, {
            layers: [clusterLayerId, clusterCountLayerId] // Se till att vi fångar upp båda lagren
          });
          if (!features.length) return;

          const clusterId = features[0].properties.cluster_id;
          map.current.getSource(sourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.current.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom + 3, // Zooma in extra nära för att spränga klustret mer tydligt
              duration: 1200 // Längd på animationen i millisekunder (öka för långsammare, standard är 500ms)
            });
          });
        });

        // Pil-pekare på kluster
        map.current.on('mouseenter', layer, (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          if (e.features.length > 0) {
            setHoveredClusterId(e.features[0].properties.cluster_id);
          }
        });

        map.current.on('mouseleave', layer, () => {
          map.current.getCanvas().style.cursor = '';
          setHoveredClusterId(null);
        });
      });

      // Hantera klick på en enskild punkt -> Visa popup
      map.current.on('click', layerId, (e) => {
        // Vi hämtar värdena ur featurens 'properties' (som vi definierade i geojsonData)
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { title, address, image, url } = e.features[0].properties;

        // Fix för utzoomning för att pointen ska landa rätt
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        const popupHTML = `
                    <div class="MapApp__popup-content" style="max-width: 200px; font-family: sans-serif;">
                        ${image ? `<img src="${image}" alt="${title}" style="width: 100%; height: auto; border-radius: 4px; margin-bottom: 8px; object-fit: cover; aspect-ratio: 16/9;" />` : ''}
                        <h4 style="margin: 0 0 4px 0; font-size: 14px;">${title || ''}</h4>
                        ${address ? `<p style="margin: 0 0 8px 0; font-size: 12px; color: #555;">${address}</p>` : ''}
                        ${url ? `<a href="${url}" target="_blank" rel="noopener noreferrer" style="font-size: 12px; color: #0056b3; text-decoration: none; font-weight: bold;">${dictionary?.product?.readMore || 'Läs mer &rarr;'}</a>` : ''}
                    </div>
                `;

        new mapboxgl.Popup({ offset: 12, closeButton: false })
          .setLngLat(coordinates)
          .setHTML(popupHTML)
          .addTo(map.current);
      });

      // Pil-pekare på enskilda punkter
      map.current.on('mouseenter', layerId, (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          setHoveredProductId(e.features[0].properties.id);
        }
      });
      map.current.on('mouseleave', layerId, () => {
        map.current.getCanvas().style.cursor = '';
        setHoveredProductId(null);
      });
    }
  }, [filteredProducts, mapLoaded]);

  // Updatera markers färg och radie när hoveredProductId ändras
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const layerId = 'products-layer';
    if (map.current.getLayer(layerId)) {
      map.current.setLayoutProperty(layerId, 'icon-size', [
        'case',
        ['==', ['get', 'id'], hoveredProductId || ''],
        0.4, // Highlight-storlek
        0.35 // Standard-storlek (20-25% mindre än kluster)
      ]);
    }
  }, [hoveredProductId, mapLoaded]);

  // Updatera kluster färg och radie när hoveredClusterId ändras
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const clusterLayerId = 'products-clusters';
    if (map.current.getLayer(clusterLayerId)) {
      map.current.setPaintProperty(clusterLayerId, 'circle-color', '#18DFBD');
      map.current.setPaintProperty(clusterLayerId, 'circle-opacity', [
        'case',
        ['==', ['get', 'cluster_id'], hoveredClusterId !== null ? hoveredClusterId : -1],
        0.8, // Lätt förstärkt vid hover
        0.5 // Standard transparens
      ]);

      map.current.setPaintProperty(clusterLayerId, 'circle-radius', [
        'case',
        ['==', ['get', 'cluster_id'], hoveredClusterId !== null ? hoveredClusterId : -1],
        [
          // Storlek vid hover (lite större)
          'step',
          ['get', 'point_count'],
          16,
          10,
          20,
          50,
          24
        ],
        [
          // Standard-storlek
          'step',
          ['get', 'point_count'],
          14,
          10,
          18,
          50,
          22
        ]
      ]);
    }
  }, [hoveredClusterId, mapLoaded]);

  const handleAreaClick = (coordinates, areaZoom) => {
    if (!map.current) return;

    let targetZoom = areaZoom;
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      targetZoom = Math.max(6, areaZoom - 1);
    }

    map.current.flyTo({
      center: coordinates,
      zoom: targetZoom,
      speed: 0.8, // Hastighet i förflyttningen (standard 1.2, lägre = långsammare)
      essential: true // this animation is considered essential with respect to prefers-reduced-motion
    });
  };

  const handleProductClick = (product) => {
    if (!map.current || !product.lat || !product.long) return;

    map.current.flyTo({
      center: [parseFloat(product.long), parseFloat(product.lat)],
      zoom: 15, // Zoom it close to focus on this single product
      speed: 0.8, // Hastighet i förflyttningen (standard 1.2, lägre = långsammare)
      essential: true
    });
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId); // Ta bort om den redan finns
      } else {
        return [...prev, categoryId]; // Lägg till i listan
      }
    });
  };

  const initialTab = initialCategories !== null ? 'EXPERIENCES' : 'NAVIGATE';

  return (
    <>
      {/* Sidebar för att visa lista/filter per Figma-design (vanlig standard layout) */}
      <div className="MapApp__sidebar-container">
        <MapSidebar
          areas={areas}
          initialTab={initialTab}
          onAreaClick={handleAreaClick}
          selectedCategories={selectedCategories}
          onSetSelectedCategories={setSelectedCategories}
          onCategoryToggle={handleCategoryToggle}
          visibleProducts={visibleProducts}
          hoveredProductId={hoveredProductId}
          onProductHover={setHoveredProductId}
          onProductClick={handleProductClick}
        />
      </div>

      {/* 100% width och height container för mapboxgl */}
      <div className="MapApp__map-container">
        {/* Mapbox kräver ett tomt container-element, så logon ligger som syskon-overlay */}
        <div ref={mapContainer} className="MapApp__map" />
        <Link
          href="/"
          className="MapApp__logo-link"
          aria-label={dictionary?.map?.backToVisitTelemark || 'Back to Visit Telemark'}
        >
          <VTLogo />
        </Link>
      </div>
    </>
  );
}
